import {cn} from '@/helpers/shadcn/utils'
import {MODAL_TYPE, SIZE, VARIANT} from '@/utils/constants/globalStyle'
import {Modal as ModalPepsico} from 'pepsico-ds'
import {useIntl} from 'react-intl'

/**
 * Modal component for displaying a customizable modal dialog.
 *
 * @param {Object} props - The properties object.
 * @param {('fluid'|'XL'|'L'|'M'|'S')} [props.type=MODAL_TYPE.FLUID] - The type of the modal.
 * @param {boolean} props.showModal - Flag to show or hide the modal.
 * @param {string} [props.title=''] - The title of the modal.
 * @param {*} [props.content=''] - The content of the modal.
 * @param {boolean} props.isDark - Flag to apply dark theme to the modal.
 * @param {string} props.className - Additional class names for the modal.
 * @param {string} [props.text=''] - Additional text for the modal.
 * @param {string} props.primaryButtonText - Text for the primary button.
 * @param {string} props.secondaryButtonText - Text for the secondary button.
 * @param {Function} props.onCloseModal - Callback function when the modal is closed.
 * @param {Function} props.onClickSecondaryButton - Callback function when the secondary button is clicked.
 * @param {Function} props.onClickPrimaryButton - Callback function when the primary button is clicked.
 * @param {string} [props.classNameWrapper=''] - Additional class names for the modal wrapper.
 * @param {boolean} [props.primaryButtonDisabled=false] - Flag to disable the primary button.
 * @param {boolean} [props.showSecondaryButton=false] - Flag to show or hide the secondary button.
 * @param {boolean} [props.showPrimaryButton=true] - Flag to show or hide the primary button.
 * @param {boolean} [props.showAuxiliaryButton=false] - Flag to show or hide the auxiliary button.
 * @param {boolean} [props.showTertiaryButton=false] - Flag to show or hide the tertiary button.
 * @param {boolean} [props.showLink=false] - Flag to show or hide a link in the modal.
 * @param {string} [props.id=''] - Optional - The id for the modal.
 * @returns {JSX.Element} The rendered Modal component.
 */
const Modal = ({
  type = MODAL_TYPE.FLUID,
  showModal,
  title = '',
  content = '',
  isDark,
  className,
  text = '',
  primaryButtonText,
  secondaryButtonText,
  secondaryButtonProps,
  onCloseModal,
  onClickSecondaryButton,
  onClickPrimaryButton,
  classNameWrapper = '',
  primaryButtonDisabled = false,
  showSecondaryButton = false,
  showPrimaryButton = true,
  showAuxiliaryButton = false,
  showTertiaryButton = false,
  showLink = false,
  tertiaryButtonText,
  auxiliaryButtonText,
  onClickTertiaryButton,
  onClickAuxiliaryButton,
  footerProps,
  id = '',
  hideCloseButton = false,
  tertiaryButtonIconLeading = 'none',
}) => {
  const {formatMessage} = useIntl()

  return (
    <ModalPepsico
      id={`modal` + (id ? `_${id}` : '')}
      type={type}
      showModal={showModal}
      isDark={isDark}
      footerProps={footerProps}
      title={title}
      text={text}
      className={className}
      showLink={showLink}
      onCloseModal={onCloseModal}
      showTertiaryButton={showTertiaryButton}
      secondaryButtonProps={{
        iconTrailing: 'none',
        size: SIZE.MEDIUM,
        text: secondaryButtonText ?? formatMessage({id: 'GENERAL.CANCEL_BUTTON'}),
        variant: VARIANT.SECONDARY,
        className: 'w-fit [&>span]:!hidden',
        onClick: onClickSecondaryButton,
        ...secondaryButtonProps,
      }}
      primaryButtonProps={{
        iconTrailing: 'none',
        size: SIZE.MEDIUM,
        text: primaryButtonText,
        variant: VARIANT.PRIMARY,
        onClick: onClickPrimaryButton,
        className: cn('w-fit [&>span]:!hidden', !primaryButtonText ? '!hidden' : ''), // ml-auto removed please add specific slass via UI schema  its affecting Media Upload
        disabled: primaryButtonDisabled,
      }}
      tertiaryButtonProps={{
        onClick: onClickTertiaryButton,
        size: 'medium',
        text: tertiaryButtonText,
        variant: 'tertiary',
        iconLeading: tertiaryButtonText ? tertiaryButtonIconLeading : '',
      }}
      auxiliaryButtonProps={{
        onClick: onClickAuxiliaryButton,
        size: 'medium',
        text: auxiliaryButtonText,
        variant: 'auxiliary',
      }}
      showSecondaryButton={showSecondaryButton}
      showPrimaryButton={showPrimaryButton}
      showAuxiliaryButton={showAuxiliaryButton}
      data-testid="modal-component"
      hideCloseButton={hideCloseButton}
    >
      {content && (
        <div data-testid="modal-wrapper" className={cn('w-full overflow-y-auto', classNameWrapper)}>
          {content}
        </div>
      )}
    </ModalPepsico>
  )
}

export default Modal
